import * as yup from 'yup';
import { Link } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useForm } from 'react-form-state-manager';

import { GetProductQuery as GetProduct } from '__generated__/graphql';

import { getServerErrors } from '../../../../common/helpers';
import UI from '../../../../common/UI';
import useEditProductMutation from './useEditProductMutation';
import useIntegerInput from '../../../../common/useIntegerInput';
import useLocale from '../../../../common/useLocale';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useProject from '../../../useProject';
import useValidation from '../../../../common/useValidation';

type Product = GetProduct['product'];
type Event = GetProduct['event'];

const editProductRestrictionsSchema = yup.object({
  capacity: yup.number().nullable(),
  min_per_ticket: yup.number().nullable(),
  max_per_ticket: yup.number().nullable().min(1),
});

export interface EditProductRestrictionsFormProps {
  product: Product;
  event: Event;
}

const EditProductRestrictionsForm = ({ product, event }: EditProductRestrictionsFormProps) => {
  const { t } = useTranslation();
  const { formatNumber } = useLocale();
  const project = useProject();
  const integerInput = useIntegerInput();

  const getValues = (product: Product) => ({
    capacity: product.capacity,
    max_per_ticket: product.max_per_ticket,
    max_per_order: product.max_per_order,
  });

  const form = useForm({
    values: getValues(product),
  });

  useNavigationPrompt(form.changed());

  const allowMaxPerTicket = !product.stand_alone;

  const [editProduct, { error, loading }] = useEditProductMutation({
    input: {
      id: product.id,
      restrictions: form.values,
    },
    onSuccess: (product) => {
      form.reset(getValues(product));
    },
  });

  const { valid, errors } = useValidation({
    schema: editProductRestrictionsSchema,
    values: form.values,
    externalErrors: getServerErrors(error, 'restrictions'),
  });

  return (
    <UI.Form onSubmit={() => editProduct()}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />

        <UI.Div>
          <UI.Legend sc={{ block: true, mb: 1 }}>
            {t('capacity')}
          </UI.Legend>
          <UI.InputDescription>
            {t('product_capacity_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
          <UI.InputGroup sc={{ valid: !errors.capacity, touched: form.touched.capacity }}>
            <UI.InputLabel htmlFor="capacity">{t('capacity')}</UI.InputLabel>
            <UI.DebouncedInput
              {...form.text('capacity', { inputHandler: integerInput() })}
              format={integerInput().format}
              maxLength={10}
              id="capacity"
              inputMode="numeric"
              match={/[\d]/g}
              placeholder="∞"
            />
            <UI.ErrorMessages attribute={t('capacity')} errors={errors.capacity} />
          </UI.InputGroup>
        </UI.FormGrid>

        {(product.product_variant_capacity !== null
          && (form.values.capacity === null || product.product_variant_capacity < form.values.capacity)) && (
          <UI.Info>
            <Trans
              i18nKey="product_variant_capacity_hint"
              values={{
                value: formatNumber(product.product_variant_capacity),
              }}
            >
              <Link to={`/${project.id}/events/${event.id}/products/${product.id}/variants`} />
            </Trans>
          </UI.Info>
        )}

        <UI.HR />

        <UI.Div>
          <UI.Legend sc={{ block: true, mb: 1 }}>
            {t('purchase_restrictions')}
          </UI.Legend>
          <UI.InputDescription>
            {t('product_restrictions_description')}
          </UI.InputDescription>
        </UI.Div>

        {allowMaxPerTicket && (
          <UI.InputGroup sc={{ valid: !errors.max_per_ticket, touched: form.touched.max_per_ticket }}>
            <UI.InputLabel htmlFor="MaxPerTicket">
              {t('max_per_ticket')}
            </UI.InputLabel>
            <UI.InputDescription>
              {t('max_per_ticket_description')}
            </UI.InputDescription>
            <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
              <UI.DebouncedInput
                {...form.text('max_per_ticket', { inputHandler: integerInput() })}
                format={integerInput().format}
                maxLength={3}
                id="MaxPerTicket"
                inputMode="numeric"
                match={/[\d]/g}
                placeholder="∞"
              />
            </UI.FormGrid>
            <UI.ErrorMessages attribute={t('max_per_ticket')} errors={errors.max_per_ticket} />
          </UI.InputGroup>
        )}

        <UI.InputGroup sc={{ valid: !errors.max_per_order, touched: form.touched.max_per_order }}>
          <UI.InputLabel htmlFor="MaxPerOrder">
            {t('max_per_order')}
          </UI.InputLabel>
          <UI.InputDescription>
            {t('max_per_order_description')}
          </UI.InputDescription>
          <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
            <UI.DebouncedInput
              {...form.text('max_per_order', { inputHandler: integerInput() })}
              format={integerInput().format}
              maxLength={3}
              id="MaxPerOrder"
              inputMode="numeric"
              match={/[\d]/g}
              placeholder="∞"
            />
          </UI.FormGrid>
          <UI.ErrorMessages attribute={t('max_per_order')} errors={errors.max_per_order} />
        </UI.InputGroup>

        <UI.Info>
          <Trans i18nKey="product_ticket_restrictions_moved_hint">
            <Link to={`/${project.id}/events/${event.id}/products/${product.id}/pricing`} />
          </Trans>
        </UI.Info>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_product')}
          closeTo={`/${project.id}/events/${event.id}/products`}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditProductRestrictionsForm;
